import React from 'react';
import { motion } from 'framer-motion';
import { User, Users, Fingerprint, Scan, ChevronRight } from 'lucide-react';
interface GenderSelectionScreenProps {
  onSelect: (gender: 'female' | 'male' | 'other') => void;
  onSkip?: () => void;
}
export function GenderSelectionScreen({
  onSelect,
  onSkip
}: GenderSelectionScreenProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} transition={{
    duration: 0.4,
    ease: [0.22, 1, 0.36, 1]
  }} className="relative z-10 w-full max-w-md mx-auto" data-id="element-1297">
      <div className="text-center mb-10" data-id="element-1298">
        <motion.div className="inline-flex items-center justify-center w-20 h-20 border border-[#FF0000]/20 bg-[#FF0000]/5 mb-6 relative overflow-hidden" initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: 0.1
      }} data-id="element-1299">
          <div className="absolute inset-0 bg-[#FF0000]/10 blur-xl" data-id="element-1300" />
          <Fingerprint size={40} className="text-[#FF0000] relative z-10 opacity-80" strokeWidth={1.5} data-id="element-1301" />

          {/* Scanning Effect */}
          <motion.div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FF0000]/20 to-transparent h-[50%]" animate={{
          top: ['-100%', '200%']
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'linear',
          repeatDelay: 1
        }} data-id="element-1302" />

          {/* Corner markers */}
          <div className="absolute top-1.5 left-1.5 w-2 h-2 border-t border-l border-[#FF0000] opacity-50" data-id="element-1303" />
          <div className="absolute top-1.5 right-1.5 w-2 h-2 border-t border-r border-[#FF0000] opacity-50" data-id="element-1304" />
          <div className="absolute bottom-1.5 left-1.5 w-2 h-2 border-b border-l border-[#FF0000] opacity-50" data-id="element-1305" />
          <div className="absolute bottom-1.5 right-1.5 w-2 h-2 border-b border-r border-[#FF0000] opacity-50" data-id="element-1306" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.4
      }} data-id="element-1307">
          <div className="inline-block px-3 py-1 mb-4 border border-[#FF0000]/20 text-[10px] font-mono text-[#FF0000] tracking-widest uppercase bg-[#FF0000]/5" data-id="element-1308">
            Subject Identification
          </div>
          <h2 className="text-3xl font-bold mb-3 leading-tight text-white tracking-tight" data-id="element-1309">
            Calibrate Analysis
          </h2>
        </motion.div>

        <motion.p className="text-sm text-gray-400 leading-relaxed max-w-xs mx-auto font-mono" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3,
        duration: 0.4
      }} data-id="element-1310">
          Select subject physiology for accurate pattern matching algorithms.
        </motion.p>
      </div>

      <motion.div className="space-y-3" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4,
      duration: 0.4
    }} data-id="element-1311">
        {[{
        id: 'female',
        label: 'Female',
        icon: User
      }, {
        id: 'male',
        label: 'Male',
        icon: User
      }, {
        id: 'other',
        label: 'Other',
        icon: Users
      }].map((option, index) => <motion.button key={option.id} onClick={() => onSelect(option.id as any)} className="group w-full relative overflow-hidden bg-white/5 hover:bg-[#FF0000]/10 border border-white/10 hover:border-[#FF0000]/50 text-gray-300 hover:text-white py-4 px-5 transition-all duration-300" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.5 + index * 0.1,
        duration: 0.4
      }} whileHover={{
        scale: 1.02,
        x: 4
      }} whileTap={{
        scale: 0.98
      }} data-id="element-1312">
            {/* Hover scan line */}
            <div className="absolute top-0 left-0 w-1 h-full bg-[#FF0000] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300" data-id="element-1313" />

            <div className="flex items-center justify-between relative z-10" data-id="element-1314">
              <div className="flex items-center gap-3" data-id="element-1315">
                <div className="p-2 bg-white/5 group-hover:bg-[#FF0000]/20 text-gray-400 group-hover:text-[#FF0000] transition-colors" data-id="element-1316">
                  <option.icon size={18} strokeWidth={2} data-id="element-1317" />
                </div>
                <span className="font-bold text-base tracking-wide" data-id="element-1318">
                  {option.label}
                </span>
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1319">
                <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase" data-id="element-1320">
                  Select
                </span>
                <Scan size={14} className="text-[#FF0000]" data-id="element-1321" />
              </div>
            </div>
          </motion.button>)}
      </motion.div>

      {onSkip && <motion.button onClick={onSkip} className="w-full mt-6 text-gray-500 text-xs font-mono uppercase tracking-widest hover:text-white transition-colors duration-200 flex items-center justify-center gap-2 group py-2" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.8,
      duration: 0.3
    }} data-id="element-1322">
          Skip to Dashboard
          <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" data-id="element-1323" />
        </motion.button>}

      <div className="mt-8 text-center" data-id="element-1324">
        <p className="text-[10px] text-gray-600 font-mono uppercase tracking-widest flex items-center justify-center gap-2" data-id="element-1325">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-1326" />
          Secure Encrypted Session • ID:{' '}
          {Math.random().toString(36).substr(2, 9).toUpperCase()}
        </p>
      </div>
    </motion.div>;
}